import "./style.scss";

import VisibilityIcon from "@mui/icons-material/Visibility";
import VisibilityOffIcon from "@mui/icons-material/VisibilityOff";
import { Collapse } from "@mui/material";
import AlertTriangle from "assets/img/svg/alertTriangle";
import StyledButton from "components/shared/StyledButton";
import { useCompanySettings } from "hooks/useCompanySettings";
import { useEffect, useMemo, useState } from "react";
import { memo } from "react";
import { useTranslation } from "react-i18next";
import type { AccountingEntry, AccountingMapping } from "store/types";
import { AccountingAccountCategory } from "store/types";

import { ComparisonTableLine } from "./ComparisonTableLine";
import { EXAMPLE_VARIABLES } from "./exampleVariables";

interface IAccountingComparisonTableProps {
    entry: AccountingEntry;
    mapping: AccountingMapping[];
}

const AccountingComparisonTable = ({ entry, mapping }: IAccountingComparisonTableProps) => {
    const [t] = useTranslation();

    const { getCompanySettings } = useCompanySettings();
    const usesIRCEC = getCompanySettings("uses_ircec");

    const filteredMapping = mapping.filter((line) =>
        [AccountingAccountCategory.GENERAL, AccountingAccountCategory.AUXILIARY].includes(line.accountCategory),
    );

    const [showExample, setShowExample] = useState(false);
    const [exampleVariables, setExampleVariables] = useState({
        ...EXAMPLE_VARIABLES,
        netToPay: usesIRCEC ? 112155 : 127155,
    });

    const foreignWithholdingExists = useMemo(() => {
        return filteredMapping.some((line) => line.accountType === "foreignWithholding");
    }, [filteredMapping]);

    const debitSum = useMemo(() => {
        const debitValues = filteredMapping
            .filter((line) => line.direction === "debit")
            .map((item) => exampleVariables[item.accountType] / 100);
        return Math.floor(debitValues.reduce((a, b) => a + b, 0));
    }, [filteredMapping, exampleVariables]);

    const creditSum = useMemo(() => {
        const creditValues = filteredMapping
            .filter((line) => line.direction === "credit")
            .map((item) => exampleVariables[item.accountType] / 100);
        return Math.floor(creditValues.reduce((a, b) => a + b, 0));
    }, [filteredMapping, exampleVariables]);

    const isComparisonGood = useMemo(() => {
        return debitSum === creditSum;
    }, [debitSum, creditSum]);

    useEffect(() => {
        setExampleVariables((prev) => ({
            ...prev,
            netToPay: foreignWithholdingExists ? 120000 : usesIRCEC ? 112155 : 127155,
        }));
    }, [foreignWithholdingExists, usesIRCEC]);

    return (
        <div className={isComparisonGood ? "typology-comparison" : "typology-comparison red"}>
            <div className="comparison-top">
                <div className="comparison-top-text">
                    <p className="bold">
                        {t("Accounting.Example.Typology")}
                        {isComparisonGood ? t("Accounting.Example.Valid") : t("Accounting.Example.NotValid")}
                    </p>
                    <p style={{ color: "rgb(141 136 136)", fontWeight: "300" }}>
                        {t("Accounting.Example.Sum")}
                        {isComparisonGood ? t("Accounting.Example.Equal") : t("Accounting.Example.Different")}
                    </p>
                </div>
                <StyledButton className="comparison-dropdown" onClick={() => setShowExample((prev) => !prev)}>
                    {!showExample ? (
                        <VisibilityIcon sx={{ fontSize: "18px", marginRight: "0.5rem" }} />
                    ) : (
                        <VisibilityOffIcon sx={{ fontSize: "18px", marginRight: "0.5rem" }} />
                    )}
                    <span>
                        {!showExample ? t("Accounting.Example.ButtonShow") : t("Accounting.Example.ButtonHide")}
                    </span>
                </StyledButton>
            </div>
            <Collapse in={showExample} timeout="auto" unmountOnExit>
                <table className="comparison-table">
                    <thead>
                        <tr>
                            <th style={{ textAlign: "left", fontWeight: "500" }}>{t("Accounting.Example.Title")}</th>
                            <th style={{ fontWeight: "300" }}>{t("Accounting.Form.Debit")}</th>
                            <th style={{ fontWeight: "300" }}>{t("Accounting.Form.Credit")}</th>
                        </tr>
                    </thead>
                    <tbody>
                        {filteredMapping.map((_mapping) => {
                            const exampleNote = entry.note
                                .replace("{{year}}", new Date().getFullYear().toString())
                                .replace("{{name}}", "Dupont")
                                .replace("{{firstname}}", "Jean")
                                .replace("{{periodStart}}", "2024-11-01")
                                .replace("{{periodEnd}}", "2025-06-30");

                            return (
                                <ComparisonTableLine
                                    line={_mapping}
                                    exampleNote={exampleNote}
                                    key={_mapping.id}
                                    value={exampleVariables[_mapping.accountType]}
                                />
                            );
                        })}
                    </tbody>
                </table>
                <div
                    className="comparison-results"
                    style={{
                        color: !isComparisonGood ? "rgb(175 114 133)" : "rgba(38, 120, 71, 1)",
                    }}
                >
                    <span>{debitSum}</span>
                    <span style={{ marginInline: "1.8rem", fontWeight: "600" }}>{isComparisonGood ? "=" : "≠"}</span>
                    <span style={{ marginRight: "2rem" }}>{creditSum}</span>
                </div>
                {!isComparisonGood && (
                    <div className="comparison-error-message">
                        <AlertTriangle width={20} height={20} color="rgb(175 114 133)" />
                        <p style={{ width: "96%" }}>{t("Accounting.Example.Message")}</p>
                    </div>
                )}
            </Collapse>
        </div>
    );
};

export default memo(AccountingComparisonTable);
